/**
 * Support Login Utilities
 *
 * Utilities for generating support login links for different environments
 * and handling support login functionality.
 */

export interface SupportLoginLinkOptions {
  phoneNumber: string;
  environment?: 'auto' | 'development' | 'production' | 'local';
}

/**
 * Determines the current environment based on the window location
 */
export const detectEnvironment = (): 'development' | 'production' | 'local' => {
  if (typeof window === 'undefined') return 'local';

  const currentHost = window.location.host;

  if (currentHost.includes('spezi.app')) {
    return 'development';
  }
  if (currentHost.includes('choco.com')) {
    return 'production';
  }
  return 'local';
};

/**
 * Gets the web domain for the given environment
 */
export const getWebDomain = (
  environment: 'development' | 'production' | 'local'
): string => {
  switch (environment) {
    case 'development':
      return 'web.spezi.app';
    case 'production':
      return 'web.choco.com';
    case 'local':
    default:
      // For local development, use web.spezi.app as the default
      // This ensures links work properly even in local development
      return 'web.spezi.app';
  }
};

/**
 * Detects if the current device is iOS
 */
export const isIOSDevice = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
};

/**
 * Generates a support login link for the given phone number
 */
export const generateSupportLoginLink = (
  options: SupportLoginLinkOptions
): string => {
  const { phoneNumber, environment = 'auto' } = options;

  if (!phoneNumber) return '';

  const env = environment === 'auto' ? detectEnvironment() : environment;
  const webDomain = getWebDomain(env);
  const isIOS = isIOSDevice();

  const encodedPhoneNumber = encodeURIComponent(phoneNumber);

  return isIOS
    ? `${webDomain}://support-login?phoneNumber=${encodedPhoneNumber}`
    : `https://${webDomain}/support-login?phoneNumber=${encodedPhoneNumber}`;
};

/**
 * Copies text to clipboard with error handling
 */
export const copyToClipboard = async (text: string): Promise<boolean> => {
  if (!text) return false;

  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    // Fallback for older browsers or non-HTTPS contexts
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    const success = document.execCommand('copy');
    document.body.removeChild(textArea);
    return success;
  } catch (error) {
    // Silently fail - error handling should be done by the calling code
    return false;
  }
};
